import * as React from "react";
import { Area, AreaChart, CartesianGrid, XAxis, LabelList, Line } from "recharts";
import { ChartConfig, ChartContainer, ChartTooltip } from "@/components/shadcn/chart";
import { Checkbox } from "@/components/shadcn/checkbox";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/shadcn/tooltip";
import { type PlayerData } from "@/api/player";
import dayjs from "dayjs";
import { dateFormat } from "@/lib/utils";
import { Loader } from "../../common/loader";
import { calculateTrendLine, getMatchInfo, TrendLabel } from "./chartUtils";
import { useIsMobile } from "@/hooks/use-mobile";

interface GoalkeeperDetailDataPoint {
    date: string;
    matchNumber: number;
    opponent: string;
    result: "W" | "D" | "L";
    score: string;
    matchDate: string;
    homeTeam: string;
    awayTeam: string;
    totalPoints: number;
    gsaxPoints: number;
    highDangerPoints: number;
    lowDangerPoints: number;
    lateralPassPoints: number;
    screenedPoints: number;
    gsAx: number;
    ga: number;
    xg_GA: number;
    savePercentage: number;
    gaZ1: number;
    xg_GAZ1: number;
    savePercentageZ1: number;
    gaZ4: number;
    xg_GAZ4: number;
    savePercentageZ4: number;
    gaScreened: number;
    xg_GAScreened: number;
    savePercentageScreened: number;
    gaLateralPass: number;
    xg_GALateralPass: number;
    savePercentageLateralPass: number;
    [key: string]: string | number;
}

function generateGoalkeeperData(playerData: PlayerData): GoalkeeperDetailDataPoint[] {
    if (!playerData?.goalkeeperKpis || playerData.goalkeeperKpis.length === 0) return [];
    const kpis = [...playerData.goalkeeperKpis].sort(
        (a, b) => new Date(a.matchDate).getTime() - new Date(b.matchDate).getTime()
    );
    return kpis.map((kpi, index) => {
        const { opponent, result, score } = getMatchInfo(
            kpi.homeTeam,
            kpi.awayTeam,
            kpi.homeGoals,
            kpi.awayGoals,
            playerData.teamId,
            kpi.homeTeamId,
            kpi.awayTeamId
        );
        return {
            date: dayjs(kpi.matchDate).format(dateFormat),
            matchNumber: index + 1,
            opponent,
            result,
            score,
            matchDate: kpi.matchDate,
            homeTeam: kpi.homeTeam,
            awayTeam: kpi.awayTeam,
            totalPoints: kpi.totalPoints,
            gsaxPoints: kpi.pointsGSAx?.pointsGSAx || 0,
            highDangerPoints: kpi.zone1?.pointsHighDangerShots || 0,
            lowDangerPoints: kpi.zone4?.pointsLowDangerShots || 0,
            lateralPassPoints: kpi.lateralPass?.pointsLateralPassShots || 0,
            screenedPoints: kpi.screened?.pointsScreenedShots || 0,
            gsAx: kpi.pointsGSAx?.gsAx || 0,
            ga: kpi.pointsGSAx?.ga || 0,
            xg_GA: kpi.pointsGSAx?.xg_GA || 0,
            savePercentage: Math.min(kpi.pointsGSAx?.savePercentage || 0, 100),
            gaZ1: kpi.zone1.gaZ1 || 0,
            xg_GAZ1: kpi.zone1.xg_GAZ1 || 0,
            savePercentageZ1: Math.min(kpi.zone1.savePercentageZ1 || 0, 100),
            gaZ4: kpi.zone4.gaZ4 || 0,
            xg_GAZ4: kpi.zone4.xg_GAZ4 || 0,
            savePercentageZ4: Math.min(kpi.zone4.savePercentageZ4 || 0, 100),
            gaScreened: kpi.screened.gaScreened || 0,
            xg_GAScreened: kpi.screened.xg_GAScreened || 0,
            savePercentageScreened: Math.min(kpi.screened.savePercentageScreened || 0, 100),
            gaLateralPass: kpi.lateralPass.gaLateralPass || 0,
            xg_GALateralPass: kpi.lateralPass.xg_GALateralPass || 0,
            savePercentageLateralPass: Math.min(kpi.lateralPass.savePercentageLateralPass || 0, 100),
        };
    });
}

const breakdownMetrics: Record<
    string,
    { title: string; metrics: { key: string; label: string; shortLabel: string; color: string }[] }
> = {
    totalPoints: {
        title: "Total Points Breakdown",
        metrics: [
            { key: "gsaxPoints", label: "GSAx Points", shortLabel: "GSAx", color: "var(--color-general-orange)" },
            {
                key: "highDangerPoints",
                label: "High Danger Points",
                shortLabel: "HD",
                color: "var(--color-general-red)",
            },
            {
                key: "lowDangerPoints",
                label: "Low Danger Points",
                shortLabel: "LD",
                color: "var(--color-general-green)",
            },
            {
                key: "lateralPassPoints",
                label: "Lateral Pass Points",
                shortLabel: "LP",
                color: "var(--color-general-yellow)",
            },
            {
                key: "screenedPoints",
                label: "Screened Points",
                shortLabel: "SCR",
                color: "var(--color-general-purple)",
            },
        ],
    },
    gsaxPoints: {
        title: "GSAx Performance",
        metrics: [
            {
                key: "savePercentage",
                label: "Save Percentage (%)",
                shortLabel: "SV%",
                color: "var(--color-general-green)",
            },
            {
                key: "gsAx",
                label: "Goals Saved Above Expected",
                shortLabel: "GSAx",
                color: "var(--color-general-orange)",
            },
            { key: "ga", label: "Goals Against", shortLabel: "GA", color: "var(--color-general-red)" },
            { key: "xg_GA", label: "Expected Goals Against", shortLabel: "xGA", color: "var(--color-general-yellow)" },
        ],
    },
    highDangerPoints: {
        title: "High Danger Zone Performance",
        metrics: [
            {
                key: "savePercentageZ1",
                label: "Save % High Danger",
                shortLabel: "SV%",
                color: "var(--color-general-green)",
            },
            { key: "gaZ1", label: "Goals Against High Danger", shortLabel: "GA", color: "var(--color-general-red)" },
            { key: "xg_GAZ1", label: "xGA High Danger", shortLabel: "xGA", color: "var(--color-general-yellow)" },
        ],
    },
    lowDangerPoints: {
        title: "Low Danger Zone Performance",
        metrics: [
            {
                key: "savePercentageZ4",
                label: "Save % Low Danger",
                shortLabel: "SV%",
                color: "var(--color-general-green)",
            },
            { key: "gaZ4", label: "Goals Against Low Danger", shortLabel: "GA", color: "var(--color-general-red)" },
            { key: "xg_GAZ4", label: "xGA Low Danger", shortLabel: "xGA", color: "var(--color-general-yellow)" },
        ],
    },
    screenedPoints: {
        title: "Screened Shots Performance",
        metrics: [
            {
                key: "savePercentageScreened",
                label: "Save % Screened",
                shortLabel: "SV%",
                color: "var(--color-general-green)",
            },
            { key: "gaScreened", label: "Goals Against Screened", shortLabel: "GA", color: "var(--color-general-red)" },
            { key: "xg_GAScreened", label: "xGA Screened", shortLabel: "xGA", color: "var(--color-general-yellow)" },
        ],
    },
    lateralPassPoints: {
        title: "Lateral Pass Shots Performance",
        metrics: [
            {
                key: "savePercentageLateralPass",
                label: "Save % Lateral Pass",
                shortLabel: "SV%",
                color: "var(--color-general-green)",
            },
            {
                key: "gaLateralPass",
                label: "Goals Against Lateral Pass",
                shortLabel: "GA",
                color: "var(--color-general-red)",
            },
            {
                key: "xg_GALateralPass",
                label: "xGA Lateral Pass",
                shortLabel: "xGA",
                color: "var(--color-general-yellow)",
            },
        ],
    },
};

function getSectionConfig(selectedKPI: string) {
    return breakdownMetrics[selectedKPI] || breakdownMetrics.totalPoints;
}

const chartConfig = {
    totalPoints: { label: "Total Points", color: "var(--color-general-blue)" },
    gsaxPoints: { label: "GSAx Points", color: "var(--color-general-orange)" },
    highDangerPoints: { label: "High Danger Points", color: "var(--color-general-red)" },
    lowDangerPoints: { label: "Low Danger Points", color: "var(--color-general-green)" },
    lateralPassPoints: { label: "Lateral Pass Points", color: "var(--color-general-yellow)" },
    screenedPoints: { label: "Screened Points", color: "var(--color-general-purple)" },
    gsAx: { label: "Goals Saved Above Expected", color: "var(--color-general-orange)" },
    ga: { label: "Goals Against", color: "var(--color-general-red)" },
    xg_GA: { label: "Expected Goals Against", color: "var(--color-general-yellow)" },
    savePercentage: { label: "Save Percentage", color: "var(--color-general-green)" },
    gaZ1: { label: "Goals Against Z1", color: "var(--color-general-red)" },
    xg_GAZ1: { label: "Expected Goals Against Z1", color: "var(--color-general-yellow)" },
    savePercentageZ1: { label: "Save Percentage Z1", color: "var(--color-general-green)" },
    gaZ4: { label: "Goals Against Z4", color: "var(--color-general-red)" },
    xg_GAZ4: { label: "Expected Goals Against Z4", color: "var(--color-general-yellow)" },
    savePercentageZ4: { label: "Save Percentage Z4", color: "var(--color-general-green)" },
    gaScreened: { label: "Goals Against (Screened)", color: "var(--color-general-red)" },
    xg_GAScreened: { label: "Expected Goals Against (Screened)", color: "var(--color-general-yellow)" },
    savePercentageScreened: { label: "Save Percentage Screened", color: "var(--color-general-green)" },
    gaLateralPass: { label: "Goals Against (Lateral Pass)", color: "var(--color-general-red)" },
    xg_GALateralPass: { label: "Expected Goals Against (Lateral Pass)", color: "var(--color-general-yellow)" },
    savePercentageLateralPass: { label: "Save Percentage Lateral Pass", color: "var(--color-general-green)" },
} satisfies ChartConfig;

interface PlayerGoalkeeperKPIDetailsChartProps {
    playerData?: PlayerData;
    selectedKPI: string;
    isLoading?: boolean;
    onTitleChange?: (title: { main: string; sub: string; subColor?: string }) => void;
}

export function PlayerGoalkeeperKPIDetailsChart({
    playerData,
    selectedKPI,
    isLoading,
    onTitleChange,
}: PlayerGoalkeeperKPIDetailsChartProps) {
    const [showTrendLine, setShowTrendLine] = React.useState(true);
    const [visibleMetrics, setVisibleMetrics] = React.useState<string[]>([]);
    const isMobile = useIsMobile();

    const isStackedView = selectedKPI === "totalPoints";
    const sectionConfig = getSectionConfig(selectedKPI);

    React.useEffect(() => {
        if (sectionConfig) {
            if (isStackedView) {
                setVisibleMetrics(sectionConfig.metrics.map((m) => m.key));
            } else {
                setVisibleMetrics([sectionConfig.metrics[0].key]);
            }
        }
    }, [selectedKPI, isStackedView, sectionConfig]);

    const chartData = React.useMemo(() => {
        if (!playerData) return [];
        const rawData = generateGoalkeeperData(playerData);
        if (rawData.length === 0) return [];
        if (showTrendLine && !isStackedView && visibleMetrics.length > 0) {
            return calculateTrendLine(rawData, visibleMetrics[0]);
        }
        return rawData;
    }, [playerData, visibleMetrics, showTrendLine, isStackedView]);

    const activeMetricColor =
        visibleMetrics.length > 0
            ? sectionConfig.metrics.find((m) => m.key === visibleMetrics[0])?.color
            : sectionConfig.metrics[0]?.color;

    const minWidthPerPoint = React.useMemo(() => {
        if (!isMobile || chartData.length === 0 || isStackedView) return 20;
        const metricKey = visibleMetrics[0];
        if (!metricKey) return 20;
        const maxLen = chartData.reduce((max, d) => {
            const v = d[metricKey];
            if (typeof v !== "number") return max;
            const str = v % 1 === 0 ? v.toString() : v.toFixed(1);
            return Math.max(max, str.length);
        }, 2);
        return Math.max(20, maxLen * 9);
    }, [chartData, visibleMetrics, isMobile, isStackedView]);

    React.useEffect(() => {
        const metricLabel = isStackedView
            ? "Breakout of Total Points"
            : sectionConfig.metrics.find((m) => m.key === visibleMetrics[0])?.label || sectionConfig.metrics[0]?.label;
        onTitleChange?.({
            main: sectionConfig.title,
            sub: metricLabel || "",
            subColor: activeMetricColor,
        });
    }, [sectionConfig, isStackedView, visibleMetrics, activeMetricColor, onTitleChange]);

    const handleMetricToggle = (metricKey: string) => {
        if (isStackedView) {
            setVisibleMetrics((prev) =>
                prev.includes(metricKey) ? prev.filter((k) => k !== metricKey) : [...prev, metricKey]
            );
        } else {
            setVisibleMetrics([metricKey]);
        }
    };

    if (isLoading && !playerData) {
        return (
            <div className="flex items-center justify-center h-full">
                <Loader message="Loading goalkeeper details chart..." className="text-sm" />
            </div>
        );
    }

    if (!playerData || chartData.length === 0) {
        return (
            <div className="flex items-center justify-center h-full">
                <p className="text-on-surface-variant">No goalkeeper detail data available</p>
            </div>
        );
    }

    return (
        <div className="p-4 flex flex-col h-full animate-delay-700">
            <div className="flex flex-wrap gap-2 mb-1 shrink-0 md:flex-col md:items-end">
                <nav className="rounded-md bg-surface-container-low p-1 basis-full md:basis-auto">
                    <div className="grid grid-cols-3 gap-1 md:flex md:items-center md:space-x-2 md:gap-0">
                        {sectionConfig.metrics.map((metric) => {
                            const isActive = visibleMetrics.includes(metric.key);
                            return (
                                <Tooltip key={metric.key}>
                                    <TooltipTrigger asChild>
                                        <button
                                            onClick={() => handleMetricToggle(metric.key)}
                                            className={`nav-tab ${isActive ? "text-white shadow-sm" : "nav-tab-inactive"}`}
                                            style={isActive ? { backgroundColor: metric.color } : {}}
                                        >
                                            {metric.shortLabel}
                                        </button>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                        <p>{metric.label}</p>
                                    </TooltipContent>
                                </Tooltip>
                            );
                        })}
                    </div>
                </nav>
                {!isStackedView && (
                    <div className="basis-full flex justify-center md:basis-auto mt-2 md:mt-0">
                        <label className="flex items-center gap-2 text-sm text-on-surface-variant cursor-pointer select-none">
                            <span>Show trend line</span>
                            <Checkbox
                                checked={showTrendLine}
                                onCheckedChange={(checked) => setShowTrendLine(checked === true)}
                                className="cursor-pointer data-[state=unchecked]:border-on-surface-variant"
                            />
                        </label>
                    </div>
                )}
            </div>

            <div className="flex-1 min-h-0 overflow-x-auto overflow-y-hidden">
                <ChartContainer
                    config={chartConfig}
                    className="h-full w-full"
                    style={{ minWidth: isMobile ? `${Math.max(300, chartData.length * minWidthPerPoint)}px` : "100%" }}
                >
                    <AreaChart
                        data={chartData}
                        margin={{
                            top: 40,
                            right: isMobile ? 8 : 30,
                            left: isMobile ? 8 : 30,
                            bottom: isMobile ? 16 : 5,
                        }}
                        stackOffset={isStackedView ? "none" : undefined}
                    >
                        <defs>
                            {sectionConfig.metrics
                                .filter((metric) => visibleMetrics.includes(metric.key))
                                .map((metric) => (
                                    <linearGradient
                                        key={metric.key}
                                        id={`fill${metric.key}`}
                                        x1="0"
                                        y1="0"
                                        x2="0"
                                        y2="1"
                                    >
                                        <stop
                                            offset="5%"
                                            stopColor={metric.color}
                                            stopOpacity={isStackedView ? 0.8 : 0.8}
                                        />
                                        <stop offset="95%" stopColor={metric.color} stopOpacity={0.1} />
                                    </linearGradient>
                                ))}
                        </defs>
                        <CartesianGrid vertical={false} stroke="rgba(148, 163, 184, 0.1)" strokeDasharray="3 3" />
                        <XAxis
                            dataKey="date"
                            tickLine={false}
                            axisLine={false}
                            tickMargin={8}
                            tick={
                                isMobile
                                    ? (props: { x?: number; y?: number; index?: number }) => (
                                          <text
                                              x={props.x}
                                              y={(props.y ?? 0) + 12}
                                              fill="#464554"
                                              fontSize={8}
                                              textAnchor="middle"
                                          >
                                              {(props.index ?? 0) + 1}
                                          </text>
                                      )
                                    : chartData.length > 20
                                      ? false
                                      : { fill: "var(--color-gray-400)", fontSize: 10 }
                            }
                            type="category"
                            interval={0}
                            padding={{ left: 20, right: 20 }}
                        />
                        <ChartTooltip
                            cursor={{
                                stroke: activeMetricColor || "var(--color-general-blue)",
                                strokeWidth: 2,
                                strokeDasharray: "5 5",
                                strokeOpacity: 0.6,
                            }}
                            content={({ active, payload, label }) => {
                                if (!active || !payload?.length) return null;
                                const dataPoint = chartData.find((item) => item.date === label);
                                if (!dataPoint) return null;
                                const visiblePayload = payload.filter((entry) =>
                                    visibleMetrics.includes(entry.dataKey as string)
                                );

                                return (
                                    <div className="chart-tooltip rounded-md min-w-[220px]">
                                        <div className="flex flex-col gap-2">
                                            <div className="font-semibold text-sm text-general-blue">
                                                Match {dataPoint.matchNumber} - {dataPoint.date}
                                            </div>
                                            <div className="text-xs text-on-surface flex items-center gap-2 flex-wrap">
                                                <span>vs {dataPoint.opponent}</span>
                                                <span
                                                    className={`px-2 py-1 rounded text-xs font-bold ${
                                                        dataPoint.result === "W"
                                                            ? "bg-win-background text-win"
                                                            : dataPoint.result === "D"
                                                              ? "bg-draw-background text-draw"
                                                              : "bg-loss-background text-loss"
                                                    }`}
                                                >
                                                    {dataPoint.result} {dataPoint.score}
                                                </span>
                                            </div>
                                            <div className="space-y-1">
                                                {visiblePayload.map((entry) => {
                                                    const metric = sectionConfig.metrics.find(
                                                        (m) => m.key === entry.dataKey
                                                    );
                                                    if (!metric) return null;
                                                    return (
                                                        <div
                                                            key={entry.dataKey}
                                                            className="flex items-center gap-2 text-xs"
                                                        >
                                                            <div
                                                                className="w-3 h-3 rounded-full"
                                                                style={{ backgroundColor: metric.color }}
                                                            />
                                                            <span className="text-on-surface">{metric.label}:</span>
                                                            <span className="font-bold" style={{ color: metric.color }}>
                                                                {typeof entry.value === "number"
                                                                    ? entry.value % 1 === 0
                                                                        ? entry.value.toString()
                                                                        : entry.value.toFixed(1)
                                                                    : entry.value}
                                                                {metric.key.includes("Percentage") ? "%" : ""}
                                                            </span>
                                                        </div>
                                                    );
                                                })}
                                                {isStackedView && (
                                                    <div className="flex items-center gap-2 text-xs border-t border-outline-variant/20 pt-1 mt-1">
                                                        <div className="w-3 h-3 rounded-full bg-on-surface-variant" />
                                                        <span className="text-on-surface">Total Points:</span>
                                                        <span className="font-bold text-on-surface-variant">
                                                            {dataPoint.totalPoints}
                                                        </span>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                );
                            }}
                        />
                        {sectionConfig.metrics
                            .filter((metric) => visibleMetrics.includes(metric.key))
                            .map((metric) => (
                                <Area
                                    key={metric.key}
                                    dataKey={metric.key}
                                    type="linear"
                                    fill={`url(#fill${metric.key})`}
                                    stroke={metric.color}
                                    strokeWidth={isStackedView ? 2 : 3}
                                    stackId={isStackedView ? "stack" : undefined}
                                    dot={isStackedView ? false : { r: 4, fill: metric.color, strokeWidth: 0 }}
                                    activeDot={
                                        isStackedView
                                            ? false
                                            : { r: 6, fill: metric.color, strokeWidth: 2, stroke: "white" }
                                    }
                                >
                                    {!isStackedView && visibleMetrics.includes(metric.key) && (
                                        <LabelList
                                            dataKey={metric.key}
                                            position="top"
                                            content={({ value, index, x, y }) => (
                                                <TrendLabel
                                                    value={value}
                                                    index={index}
                                                    x={x}
                                                    y={y}
                                                    dataKey={metric.key}
                                                    data={chartData}
                                                />
                                            )}
                                        />
                                    )}
                                </Area>
                            ))}
                        {!isStackedView &&
                            showTrendLine &&
                            visibleMetrics.map((metricKey) => (
                                <Line
                                    key={`${metricKey}_trend`}
                                    dataKey={`${metricKey}_trend`}
                                    type="linear"
                                    stroke="var(--color-chart-ref-line)"
                                    strokeWidth={3}
                                    strokeDasharray="5 5"
                                    dot={false}
                                    activeDot={false}
                                />
                            ))}
                    </AreaChart>
                </ChartContainer>
            </div>
        </div>
    );
}
